import { ReactElement } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IconButton } from "@/DesignSystem";

import styles from "./DeletableItemsList.module.scss";

import XRegularIcon from "@/DesignSystem/Atoms/Icons/Types/X/x-regular.svg";

/**
 * Represents a deletable item with an ID and a name.
 * This is used to render a list of items that can be deleted.
 * @template T The type of the unique identifier for the item.
 */
interface IDeletableItem<T> {
    /**
     * The unique identifier of the item.
     * This is used as the key for rendering the item in a list.
     * @type {T}
     */
    Id: T;

    /**
     * The name of the item.
     */
    Name: string;
}

/**
 * The properties for a {@link DeletableItemsList} component.
 * This component is used to render a list of items that can be deleted.
 * @template T The type of the unique identifier for the item.
 */
interface IDeletableItemsListProps<T> {
    /**
     * A callback invoked when the DeletableItem delete button is clicked.
     * @param deletableItem The clicked DeletableItem.
     */
    onDeleteClick: (deletableItem: IDeletableItem<T>) => void;

    /**
     * DeletableItems list
     */
    deleteableItems: IDeletableItem<T>[];
}

/**
 * A horizontal list of flowsheets.
 * @param props The FlowsheetsList properties.
 * @template T The type of the unique identifier for the item.
 * @returns {ReactElement} The rendered component.
 */
function DeletableItemsList<T>({ onDeleteClick, deleteableItems }: Readonly<IDeletableItemsListProps<T>>): ReactElement {
    const { getText } = useTranslation();

    return (
        <ul className={styles.flowsheetList}>
            {deleteableItems?.map(deleteableItem => (
                <li
                    key={`deletable-items-list_${deleteableItem.Id}`}
                    className={styles.deleteableItemsListItem}
                >
                    <span>
                        {deleteableItem.Name}
                    </span>
                    <IconButton
                        variant="error"
                        category="tertiary"
                        size="sm"
                        onClick={() => onDeleteClick(deleteableItem)}
                        dataCy={`flowsheet-deleteable-template-${deleteableItem.Name}`}
                        aria-label={getText("Flowsheets_DeleteLabel")}
                    >
                        <XRegularIcon aria-label={getText("Delete")} />
                    </IconButton>
                </li>
            ))}
        </ul>
    );
}

export default DeletableItemsList;

export type { IDeletableItem, IDeletableItemsListProps };
